import { InputText } from 'primeng/inputtext';
import { TranslateService } from '@ngx-translate/core';
import { filter, debounceTime, distinctUntilChanged } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  input,
  inject,
  OnInit,
  output,
  effect,
  computed,
  Component,
  DestroyRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SearchSize, SearchColor } from './types';

import { BooleanTransformer } from '@/app/shared/utils';

import { SvgIcon } from '../svg-icon';
import { ProgressSpinner } from '../../ui/progress-spinner';

@Component({
  selector: 'app-search',
  standalone: true,
  templateUrl: './search.html',
  styleUrl: './search.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'searchClasses()',
    '[class.loading]': 'loading()',
  },
  imports: [ProgressSpinner, SvgIcon, InputText, ReactiveFormsModule],
})
export class Search implements OnInit {
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  public size = input<SearchSize>('small');

  public loading = input(false, { transform: BooleanTransformer });

  public color = input<SearchColor>('primary');

  public placeholder = input<string>(this.translateService.instant('shared.ui.search.placeholder'));

  public debounceTime = input<number>(500);

  public minLength = input<number>(1);

  public clearTrigger = input<number>(0);

  public searchChange = output<string>();

  public searchClasses = computed(() => `search ${this.size()} ${this.color()}`);

  searchControl = new FormControl('');

  constructor() {
    effect(() => {
      const trigger = this.clearTrigger();
      if (trigger > 0) {
        this.searchControl.setValue('', { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        filter((value) => {
          const minLen = this.minLength();
          return value !== null && value.trim().length >= minLen;
        }),
        debounceTime(this.debounceTime()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (value) => {
          this.searchChange.emit(value!.trim());
        },
        error: (error) => {
          console.error('Search error:', error);
        },
      });
  }

  resetSearch() {
    this.searchControl.reset();
    this.searchChange.emit('');
  }
}
